import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { PhotoSlot } from "@/components/site/PhotoSlot";
import { staggerContainer, fadeUp, IMAGES } from "@/lib/motion";

const CHAPTERS = [
  {
    n: "01",
    title: "100+ Direct Linkages",
    body: "Producer co-operatives, smallholder farmers and processing facilities across India \u2014 a network we\u2019ve spent years earning our way into.",
  },
  {
    n: "02",
    title: "No Middlemen",
    body: "Direct farm and plant relationships. We buy where it\u2019s grown and where it\u2019s made \u2014 protecting margin, quality and traceability.",
  },
  {
    n: "03",
    title: "Reliable Logistics",
    body: "A solid multi-modal logistic network delivering seamless, timely, cost-effective shipments across Assam, West Bengal, Bihar, Meghalaya and beyond.",
  },
  {
    n: "04",
    title: "Zero Rejections",
    body: "Real-time updates on loading schedules, dispatch and material specs \u2014 zero rejections and zero detention charges, across every sector we\u2019ve served.",
  },
];

export const Manifesto = () => (
  <section
    id="about"
    data-testid="manifesto-section"
    className="relative bg-[#F0E6C0] py-24 lg:py-40"
  >
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      {/* Section intro */}
      <Reveal className="mb-20 lg:mb-28 max-w-4xl">
        <p className="mb-5 text-xs md:text-sm uppercase tracking-[0.25em] text-[#7A4E24]">
          &#8212; Who We Are &amp; Why We Exist
        </p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.9] tracking-tight text-[#3D2817]">
          Your bridge to
          <span className="block italic text-[#7A4E24]">North-East India&apos;s finest.</span>
        </h2>
      </Reveal>

      <div className="grid grid-cols-12 gap-y-16 lg:gap-x-16">
        {/* Left: narrative */}
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <p className="text-base md:text-lg leading-relaxed text-[#6B4423]">
              Specialised sourcing partners for merchant importers across
              Vietnam, Nepal, Bangladesh &amp; other SE Asian countries,
              animal feed mills, solvent extraction plants, ethanol plants,
              grain-based distilleries, oil &amp; oleoresin extraction plants
              and spice manufacturers &mdash; alongside retail &amp; wholesale
              traders. <span className="text-[#3D2817] font-medium">Zero rejections across all sectors, till date.</span>
            </p>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#6B4423]">
              North-East India&apos;s most prized agricultural territories
              remain locked behind geography and trust barriers. We&apos;ve
              spent years building relationships in these tribal heartlands
              &mdash; with farmer collectives, local cooperatives and growers
              who guard their finest produce.
            </p>
            <div className="mt-10">
              {IMAGES.facility ? (
                <div className="overflow-hidden rounded-2xl border border-[#3D2817]">
                  <motion.img
                    src={IMAGES.facility}
                    alt="North-East India agricultural landscape"
                    className="h-80 w-full object-cover"
                    loading="lazy"
                    initial={{ scale: 1.2 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
              ) : (
                <PhotoSlot
                  testId="manifesto-photo-slot"
                  caption="North-East India"
                  label="Facility / landscape photo"
                  height="h-80"
                />
              )}
            </div>
          </Reveal>
        </div>

        {/* Right: chapters */}
        <motion.div
          className="col-span-12 lg:col-span-7 lg:pl-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {CHAPTERS.map((c) => (
            <motion.div
              key={c.n}
              variants={fadeUp}
              data-testid={`manifesto-chapter-${c.n}`}
              className="grid grid-cols-12 items-start gap-4 border-t border-[#DBCFA0] py-10 first:border-t-0 lg:py-12"
            >
              <div className="col-span-3 md:col-span-4">
                <span className="font-display text-6xl md:text-8xl leading-none text-[#7A4E24]/40">
                  {c.n}
                </span>
              </div>
              <div className="col-span-9 md:col-span-8">
                <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#3D2817]">
                  {c.title}
                </h3>
                <p className="mt-4 max-w-xl text-base md:text-lg leading-relaxed text-[#6B4423]">
                  {c.body}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);
